import React, { useEffect, useState } from 'react';
import SquareIconButton from '../../atoms/Button/SquareIconButton';
import { ColumnDef } from '../../atoms/Table/tableFeatures';
import ExpandableCard from '../../atoms/Card/ExpandableCard';
import CardBody from '../../atoms/Card/CardBody';
import Table from '../../atoms/Table/Table';
import {
    PeriodicReportsState,
    clearDownloadCsvFailed,
} from '../../../store/report/periodicReports/periodicReportsSlice';
import periodicReportActions from '../../../store/report/periodicReports/actions';
import { useAppState } from '../../../store/appstate';
import { PeriodicReport, PeriodicReportStatus } from '../../../types/report/PeriodicReports';
import { TableRowContent } from '../../../types/Layout/TableRowContent';
import { useAppDispatch } from '../../../store';
import { formatShortDate } from '../../../services/helpers/dateTimeFormats';
import Flex from '../../atoms/Box/Flex';
import Button from '../../atoms/Button/Button';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import Spinner from '../../atoms/Spinner/Spinner';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import { ErrorType } from '../../../types/response/ErrorCodes';
import Snackbar from '../../atoms/Snackbar/Snackbar';
import EmptyState from '../../atoms/Information/EmptyState';
import FileChartLineIcon from 'remixicon-react/FileChartLineIcon';
import Download2LineIcon from 'remixicon-react/Download2LineIcon';
import { PeriodicReportType } from '../../../types/report/PeriodicReports';
import { parseSearchUrl } from '../../../services/helpers/queryStringHelpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { browsePaginationWithDispatch } from '../../../services/helpers/pagination';
import SelectFilter from '../../atoms/SelectFilter/SelectFilter';

interface PeriodReportsProps {
    theme: DefaultTheme;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
}
interface PeriodicReportForTable extends PeriodicReport, TableRowContent {}

const camelCaseToReadableString = (value: string) =>
    value.replace(/([a-z])([A-Z])/g, (_, lower, upper) => `${lower} ${upper.toUpperCase()}`);

const getReportTypeName = (value: string) => {
    if (value == PeriodicReportType.RESERVATIONS) return 'Company Customer Credit Limits';
    return camelCaseToReadableString(value);
};

const PeriodicReports: React.FC<PeriodReportsProps> = ({
    theme,
    isExpanded,
    onExpandToggle,
}: PeriodReportsProps) => {
    const dispatch = useAppDispatch();
    const {
        pagedReports,
        isLoading,
        getReportsError,
        downloadCsvFailed,
        downloadCsvProcessing,
        reportDownloadToken,
    } = useAppState<PeriodicReportsState>(s => s.report.periodicReports);
    const navigate = useNavigate();
    const { pathname, search: queryString } = useLocation();
    const { page, perPage } = parseSearchUrl(queryString);
    const [daysBack, setDaysBack] = useState('7');

    const buildReportsUrl = (p: string, pp: string, days?: string) => {
        const base = `/periodic-reports?page=${p}&perPage=${pp}`;
        if (days) return `${base}&filter=creationDate:["-${days}d"]`;
        return base;
    };

    const columns: ColumnDef<PeriodicReportForTable>[] = [
        {
            header: 'Name',
            accessorKey: 'name',
        },
        {
            header: 'Period',
            accessorKey: 'period',
            cell: (props: { row: { original: PeriodicReport } }) => {
                const { period } = props.row.original;

                if (!period) {
                    return '-';
                }

                const formattedStartDate = formatShortDate(new Date(period.start));
                const formattedEndDate = formatShortDate(new Date(period.end));
                return `${formattedStartDate}-${formattedEndDate}`;
            },
        },
        {
            header: 'Creation date',
            accessorKey: 'creationDate',
            cell: (props: { row: { original: PeriodicReport } }) => {
                return props.row.original.creationDate
                    ? formatShortDate(new Date(props.row.original.creationDate))
                    : '-';
            },
        },
        {
            header: 'Expiration date',
            accessorKey: 'expirationDate',
            cell: (props: { row: { original: PeriodicReport } }) => {
                return formatShortDate(new Date(props.row.original.expirationDate));
            },
        },
        {
            header: 'Type',
            accessorKey: 'type',
            cell: (props: { row: { original: PeriodicReport } }) => {
                return getReportTypeName(camelCaseToReadableString(props.row.original.type));
            },
        },
        {
            header: 'Created by',
            accessorKey: 'createdBy',
        },
        {
            header: 'Status',
            accessorKey: 'status',
        },
        {
            header: 'Download',
            id: 'downloadReport',
            accessorKey: 'download',
            cell: (props: { row: { original: PeriodicReport } }) => {
                return (
                    props.row.original.status === PeriodicReportStatus.DONE && (
                        <SquareIconButton
                            onClick={() => onRowCsvClicked(props.row.original.id)}
                            title="Download csv"
                            data-testid="download-report"
                        >
                            <Download2LineIcon size={15} />
                            Csv
                        </SquareIconButton>
                    )
                );
            },
        },
    ];

    const defaultColumns = columns.map(col => col.header?.toString() ?? '');

    const onRowCsvClicked = (id: string) => {
        dispatch(periodicReportActions.getReportDownloadToken(id));
    };

    useEffect(() => {
        dispatch(periodicReportActions.getPagedReports(buildReportsUrl(page, perPage, daysBack)));
    }, []);

    const handleDaysBackChange = (newDaysBack: string) => {
        setDaysBack(newDaysBack);
        dispatch(periodicReportActions.getPagedReports(buildReportsUrl('1', perPage, newDaysBack)));
    };

    useEffect(() => {
        if (reportDownloadToken) {
            const link = document.createElement('a');
            link.href = `/report?token=${reportDownloadToken}`;
            link.download = 'report.csv';

            document.body.appendChild(link);

            link.click();
            dispatch(clearDownloadCsvFailed());
        }
    }, [reportDownloadToken]);

    if (getReportsError) {
        return <ErrorMessage error={getReportsError} errorHeader={ErrorType.LoadReports} />;
    }

    return (
        <>
            <div translate="no">
                <Flex
                    alignItems="flex-end"
                    row
                    justifyContent="flex-start"
                    style={{ height: '100%' }}
                    gap={theme.layout.gap.medium}
                >
                    <Button
                        onClick={() =>
                            dispatch(
                                periodicReportActions.getPagedReports(
                                    buildReportsUrl(page, perPage, daysBack)
                                )
                            )
                        }
                    >
                        Refresh reports
                    </Button>
                    <CreatedDateFilterWrapper>
                        <Label>Show reports from the last</Label>
                        <SelectFilter
                            options={['7', '14', '30', '60', '90']}
                            value={daysBack}
                            onChange={handleDaysBackChange}
                            testId="created-date-filter"
                        />
                        <Label>days</Label>
                    </CreatedDateFilterWrapper>
                </Flex>
            </div>
            <div translate="no">
                {downloadCsvFailed && (
                    <Snackbar
                        clear={() => dispatch(clearDownloadCsvFailed())}
                        open={downloadCsvFailed}
                        color={theme.colors.snackbar.error.background}
                        message="Could not download file. Try again later"
                    />
                )}
                <ExpandableCard expanded={isExpanded}>
                    <CardBody>
                        {isLoading && (
                            <DownloadSpinnerWrapper>
                                <Spinner text="Getting reports" loading={isLoading} />
                            </DownloadSpinnerWrapper>
                        )}
                        {downloadCsvProcessing && (
                            <DownloadSpinnerWrapper>
                                <Spinner text="Downloading..." loading={downloadCsvProcessing} />
                            </DownloadSpinnerWrapper>
                        )}
                        <TableWrapper isLoading={isLoading || downloadCsvProcessing}>
                            <Table<PeriodicReportForTable>
                                key={'periodicReports'}
                                allColumns={columns}
                                columns={columns}
                                data={pagedReports?.data as PeriodicReportForTable[]}
                                defaultColumns={defaultColumns}
                                pagination={{
                                    links: pagedReports.links,
                                    metaData: pagedReports.metaData,
                                    pageSize: perPage,
                                    nextPage: () =>
                                        browsePaginationWithDispatch(
                                            navigate,
                                            pathname,
                                            `${pagedReports.links.next?.query}`,
                                            periodicReportActions.getPagedReports(
                                                pagedReports.links.next?.href ?? `/periodic-reports`
                                            ),
                                            dispatch
                                        ),
                                    previousPage: () =>
                                        browsePaginationWithDispatch(
                                            navigate,
                                            pathname,
                                            `${pagedReports.links.prev?.query}`,
                                            periodicReportActions.getPagedReports(
                                                pagedReports.links.prev?.href ?? `/periodic-reports`
                                            ),
                                            dispatch
                                        ),
                                }}
                                isExpanded={isExpanded}
                                onExpandToggle={onExpandToggle}
                            />
                        </TableWrapper>
                        {!isLoading && pagedReports?.data && pagedReports.data.length === 0 && (
                            <EmptyState
                                title={"It's empty over here!"}
                                icon={
                                    <FileChartLineIcon
                                        size={theme.icon.size.xlarge}
                                        color={theme.colors.accent3}
                                    />
                                }
                            >
                                <div>
                                    No reports found. Try selecting a longer time period or create a
                                    new report.
                                </div>
                            </EmptyState>
                        )}
                    </CardBody>
                </ExpandableCard>
            </div>
        </>
    );
};

export default withTheme(PeriodicReports) as React.ComponentType<Omit<PeriodReportsProps, 'theme'>>;
interface StyleProps {
    isLoading: boolean;
}

const DownloadSpinnerWrapper = styled.div`
    position: fixed;
    left: 50%;
    top: 35%;
    margin-left: -1rem;
    margin-top: 10rem;
    z-index: 8;
`;

const TableWrapper = styled.div<StyleProps>`
    opacity: ${props => (props.isLoading ? 0.5 : 1)};
`;

const CreatedDateFilterWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const Label = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;
